import { useCMSPageField } from '@/cms/fields'
import { type CMSMedia, CMSMediaType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { useAddFavoriteFromUrl } from '@/domain/product/hooks'

import { buildImgSrc } from '@/helpers/image-url'

import { useIntl } from '@/intl'
import { useOnScreen } from '@/scopes/common/components/hooks/useOnScreen'
import { useOptimizelyFlagContext } from '@/scopes/common/infrastructure/ab-testing/useOptimizely'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import * as AspectRatio from '@radix-ui/react-aspect-ratio'

import { Icon, Image, Label, LazyLoader, Modal, tk, Typography } from '@sunwebgroup/ui-components'
import { type ComponentPropsWithoutRef, useEffect, useRef, useState } from 'react'
import { css, styled } from 'styled-components'
import { Detail } from './Detail'
import FavoriteButton from './Favorite/FavoriteButton'
import { GalleryLoading } from './GalleryLoading'

interface GalleryProps {
  props?: ComponentPropsWithoutRef<'ol'>
  isFullReact?: boolean
}

const ImageElement = styled(Image.Root)`
  @media ${tk('mq_tablet')} {
    border-radius: ${tk('radius_large')};
    &:nth-child(n + 6) {
      display: none;
    }
  }

  &:nth-child(n + 4) {
    display: none;
  }

  object-fit: cover;
  height: 100%;
  width: 100%;
  display: block;
`

const ImageElementModal = styled(Image.Root)`
  object-fit: cover;
  height: 100%;
  width: 100%;
  display: block;
`

const ImageElementButtonModal = styled.button`
  display: block;
  width: 100%;
  height: 100%;
  overflow: hidden;
`

const LazyLoaderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: grid;
`

const CollectionLabel = styled(Label)<{ $textColor: string, $backgroundColor: string }>`
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;

  @media ${tk('mq_tablet')} {
    border-top-left-radius: ${tk('radius_large')};
  }

  ${({ $textColor, $backgroundColor }) => css`
    background: ${$backgroundColor};
    color: ${$textColor};
  `}
`

const StyledPlay = styled.div`
  position: absolute;
  bottom: 50%;
  right: 50%;
  transform: translate(50%, 50%);
  background-color: ${tk('color_palette_transparent_white_75')};
  border-radius: ${tk('radius_rounded')};
`

const ImageError = styled(Image.Error)``

export const ImageElementButton = styled.button`
  position: relative;

  &:hover {
    ${ImageElement} img {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }

    ${ImageError} {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      background-color: ${tk('color_semantic_bg')};
      color: ${tk('color_semantic_icon_hover')};
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    ${ImageElement} img {
      transition: all 0.3s ease-in-out;
    }

    ${ImageError} {
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      transition: all 0.3s ease-in-out;
    }
  }
`

const StyledPlayIcon = styled(Icon)`
  width: auto;
  padding: ${tk('spacing_sm')};
  position: relative;
  left: 2px; // To center the icon
`

const StyledGridModal = styled.ol`
  display: flex;
  flex-wrap: wrap;
  max-width: 864px;
  margin: 0 auto;
  list-style: none;
  gap: ${tk('spacing_xs')};
  padding: ${tk('spacing_md')};
  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_lg')};
  }
  @media ${tk('mq_desktop_sm')} {
    padding: ${tk('spacing_2xl')} 0;
  }
`

const StyledImageWrapperModal = styled.li`
  width: calc(50% - (${tk('spacing_xs')} / 2));

  &:first-child,
  &:nth-child(3n + 1) {
    width: 100%;
  }

  &:hover {
    ${ImageElementModal} img {
      filter: brightness(0.85);
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      background-color: ${tk('color_semantic_bg')};
      color: ${tk('color_semantic_icon_hover')};
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    ${ImageElementModal} img {
      transition: all 0.3s ease-in-out;
    }

    ${StyledPlay} {
      transition: all 0.3s ease-in-out;
    }
  }
`

const Wrapper = styled.ol<{ $isFullReact: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  height: 350px;
  list-style: none;
  margin-left: unset;
  ${({ $isFullReact }) => {
    if ($isFullReact) {
      return css`
        margin-bottom: ${tk('spacing_md')};
      `
    }
    return css`
      margin-bottom: ${tk('spacing_2xl')};
    `
  }}

  padding: 0;

  /**
   This is here to hide the "old" collection label in the PDP when the react
   version of the gallery is present.

   TODO: Remove after all PDP is migrated to react
   */
  z-index: 1;
  position: relative;
  /* **** */

  overflow: hidden;
  border-radius: ${tk('radius_large')};

  @media ${tk('mq_tablet')} {
    gap: ${tk('spacing_sm')};
    grid-gap: ${tk('spacing_sm')};
    grid-template-columns: repeat(4, 1fr);
    height: 425px;
    margin-bottom: unset;
  }
`

const StyledPill = styled.div`
  position: absolute;
  display: flex;
  width: unset !important;
  height: unset !important;
  background-color: ${tk('color_palette_grey_800')}50;
  border-radius: ${tk('radius_rounded')};
  color: ${tk('color_semantic_text_on_color')};
  font-size: ${tk('font_size_xs')};
  padding: ${tk('spacing_xs')};
  align-items: center;
  bottom: ${tk('spacing_sm')};
  right: ${tk('spacing_md')};

  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_2xs')} ${tk('spacing_sm')};
  }
`

const MainGridWrapper = styled.li`
  position: relative;
  display: block;
  height: 100%;
  width: 100%;

  &:first-child {
    grid-column: span 2;
    @media ${tk('mq_tablet')} {
      grid-row: span 2;
    }

    ${StyledPill} {
      display: none;

      @media ${tk('mq_tablet')} {
        display: flex;
      }
    }
  }

  &:nth-child(n + 4) {
    display: none;

    @media ${tk('mq_desktop_sm')} {
      display: block;
    }
  }

  &:nth-child(2),
  &:nth-child(3) {
    @media ${tk('mq_tablet')} {
      grid-column: span 2;
    }
    @media ${tk('mq_desktop_sm')} {
      grid-column: span 1;
    }
  }

  ${ImageElementButton} {
    display: block;
    height: 100%;
    width: 100%;
  }

  ${StyledPill} {
    display: flex;

    @media ${tk('mq_tablet')} {
      display: none;
    }
  }

  &:hover {
    ${StyledPlay} {
      background-color: ${tk('color_semantic_bg')};
      color: ${tk('color_semantic_icon_hover')};
      transition: all 0.3s ease-in-out;
    }
  }

  &:not(:hover) {
    ${StyledPlay} {
      transition: all 0.3s ease-in-out;
    }
  }
`

const StyledPillIcon = styled(Icon)`
  width: auto;
`

const StyledPillText = styled(Typography)`
  text-align: left;
  padding-left: ${tk('spacing_xs')};
`

const FavoriteContainer = styled.div`
  position: absolute;
  top: ${tk('spacing_xs')};
  right: ${tk('spacing_xs')};
`

interface SentinelProps {
  onVisible: () => void
}

/**
 * A sentinel component that triggers a callback when it becomes visible on the screen.
 * @param onVisible - The callback function to be called when the sentinel becomes visible.
 * @returns The sentinel component.
 */
function Sentinel({ onVisible }: SentinelProps) {
  const sentinelRef = useRef<HTMLDivElement>(null)
  const isSentinelVisible = useOnScreen(sentinelRef)

  useEffect(() => {
    if (isSentinelVisible) {
      onVisible()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We know onVisible is not going to change.
  }, [isSentinelVisible])

  return <div ref={sentinelRef} />
}

const StyledSentinel = styled(Sentinel)`
  background: transparent;
  height: 1px;
  margin-bottom: -1px;
  width: 100%;
`

export default function Gallery({ props, isFullReact = false }: GalleryProps) {
  const intl = useIntl()

  const { noGalleryVideosDecisionReady, noGalleryVideosDecision } = useOptimizelyFlagContext()
  const allMedia = useCMSPageField('PDP.media')
  const media: CMSMedia[] = []
  let videosOnMedia: boolean = false
  for (const item of allMedia) {
    if (item.type === CMSMediaType.VIDEO) {
      videosOnMedia = true
    }
    if (item.type !== CMSMediaType.VIDEO || !noGalleryVideosDecision) {
      media.push(item)
    }
  }
  const collectionName = useCMSPageField('PDP.collectionName')
  const collectionTheme = useCMSPageField('PDP.collectionTheme')
  const contextItemId = useCMSPageField('contextItemId')

  useAddFavoriteFromUrl(contextItemId)

  const maxElements = 5
  const noContentFallbackText = intl.formatMessage({ id: 'no_image' })
  const modalTitle = intl.formatMessage({ id: 'accommodation_page_gallery_modal_title' })

  const [showModal, setShowModal] = useState(false)
  const [activeItem, setActiveItem] = useState(0)
  const [showDetail, setShowDetail] = useState(false)
  const [clickedItemIndex, setClickedItemIndex] = useState(0)
  const { sendEvent } = useAnalytics()
  const [show, setShow] = useState(false)

  const pillCaption: string = intl.formatMessage(
    {
      id:
        media.length > 1
          ? 'accommodation_page_gallery_pill_caption_plural'
          : 'accommodation_page_gallery_pill_caption_singular',
    },
    { num_elements: media.length },
  )

  useEffect(() => {
    if (showModal) {
      window.requestAnimationFrame(() => {
        const element = document.getElementById(`gallery-element-${activeItem}`)
        if (element) {
          element.scrollIntoView()
        }
      })
    }

    setShow(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run this effect when the modal is opened
  }, [showModal])

  const closeModal = () => {
    setShowModal(false)
  }

  const openModal = (index: number) => {
    setShowModal(true)
    setActiveItem(index)
  }

  const getAspectRatio = (index: number) => {
    return index % 3 !== 0 ? 1 : 2
  }

  const closeDetail = () => {
    setShowDetail(false)
  }

  const openDetail = (index: number) => {
    setClickedItemIndex(index)
    setShowDetail(true)
  }

  const firstMedia = media.slice(0, maxElements)

  const handleImageClick = (index: number) => {
    openModal(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Collection gallery - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleDetailImageClick = (index: number) => {
    openDetail(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Element clicked',
        description: `Photo Gallery`,
        result: 'Image slider - Opens',
        type: `${index + 1}`,
      }),
    )
  }

  const handleSentinelVisible = (): void => {
    sendEvent(
      createPDPClickEvent({
        element: 'Scroll',
        description: `Photo Gallery`,
        result: 'Collection gallery - Scroll to bottom',
      }),
    )
  }

  if (!show || !noGalleryVideosDecisionReady)
    return <GalleryLoading />

  if (videosOnMedia && noGalleryVideosDecisionReady) {
    sendEvent({
      event: 'CRO',
      description: 'ID 410 | video on pdp',
      element: 'NL',
      type: 'tracking',
      result: noGalleryVideosDecision ? 'Variant' : 'Default',
      interaction: false,
    })
  }

  if (!media || media.length === 0)
    return null

  return (
    <>
      <Wrapper {...props} className="accommodation-carousel" $isFullReact={isFullReact}>
        {firstMedia.map((element, index) => (
          <MainGridWrapper key={index}>
            <ImageElementButton onClick={() => handleImageClick(index)}>
              <ImageElement
                defaultSource={{ src: buildImgSrc(element.src, 480, 360), width: 480, height: 360 }}
                loaderSource={{ src: buildImgSrc(element.src, 20) }}
                sources={
                  index === 0
                    ? [
                        {
                          src: buildImgSrc(element.src, 1280, 960),
                          width: 1280,
                          height: 960,
                          breakpoint: 'breakpoint_tablet',
                        },
                      ]
                    : []
                }
                alternativeText={element.alt || ''}
              >
                <ImageError>
                  <Icon glyph="IconNoImage" size="lg" />
                  <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
                </ImageError>
              </ImageElement>
              {(index === 0 || index === 2) && ( // For handling the pill in desktop and mobile
                <StyledPill>
                  <StyledPillIcon glyph="IconImage" size="sm" aria-hidden />
                  {index === 0 && <StyledPillText $variant="body-md">{pillCaption}</StyledPillText>}
                </StyledPill>
              )}
              {element.type === CMSMediaType.VIDEO && (
                <StyledPlay>
                  <StyledPlayIcon glyph="IconPlay" size="md" aria-hidden />
                </StyledPlay>
              )}
              {index === 0 && collectionName && (
                <CollectionLabel
                  $textColor={collectionTheme.text}
                  $backgroundColor={collectionTheme.background}
                  $variant="corner"
                >
                  {collectionName}
                </CollectionLabel>
              )}
            </ImageElementButton>
            {index === 0 && (
              <FavoriteContainer>
                <FavoriteButton />
              </FavoriteContainer>
            )}
          </MainGridWrapper>
        ))}
      </Wrapper>
      <Modal.Root open={showModal} onClose={closeModal}>
        <Modal.Content $size="fullscreen">
          <Modal.Header>
            <Modal.HeaderTitle text={modalTitle} />
            <Modal.HeaderCloseButton />
          </Modal.Header>
          <Modal.Body>
            <StyledGridModal>
              {media.map((mediaElement, index) => (
                <StyledImageWrapperModal key={index}>
                  <AspectRatio.Root ratio={getAspectRatio(index)}>
                    <ImageElementButtonModal onClick={() => handleDetailImageClick(index)}>
                      <div id={`gallery-element-${index}`} />

                      <LazyLoader>
                        {({ inView }) => (
                          <LazyLoaderWrapper>
                            <ImageElementModal
                              defaultSource={{ src: buildImgSrc(mediaElement.src, 480, 360), width: 480, height: 360 }}
                              loaderSource={{ src: buildImgSrc(mediaElement.src, 20) }}
                              sources={
                                index % 3 === 0
                                  ? [
                                      {
                                        src: buildImgSrc(mediaElement.src, 1280, 960),
                                        width: 1280,
                                        height: 960,
                                        breakpoint: 'breakpoint_tablet',
                                      },
                                    ]
                                  : []
                              }
                              alternativeText={mediaElement.alt || ''}
                              startImmediateLoad={inView}
                            >
                              <Image.Error>
                                <Icon glyph="IconNoImage" size="lg" />
                                <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
                              </Image.Error>
                            </ImageElementModal>
                          </LazyLoaderWrapper>
                        )}
                      </LazyLoader>
                      {mediaElement.type === CMSMediaType.VIDEO && (
                        <StyledPlay>
                          <StyledPlayIcon glyph="IconPlay" size="md" aria-hidden />
                        </StyledPlay>
                      )}
                    </ImageElementButtonModal>
                  </AspectRatio.Root>
                </StyledImageWrapperModal>
              ))}
              <StyledSentinel onVisible={handleSentinelVisible} />
            </StyledGridModal>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
      <Detail {...props} onClose={closeDetail} open={showDetail} startFromIndex={clickedItemIndex} />
    </>
  )
}

Gallery.GalleryElement = Wrapper
